// ABOUTME: Catalog configuration overview styled like Order Management — white card table + Add button (HCP-111556).
import {Content, ResponsiveTable, Typography} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import UploadIcon from '@mui/icons-material/FileUploadOutlined'
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
} from '@mui/material'
import React, {useState} from 'react'
import {useHistory} from 'react-router-dom'

import {useAllowedHConnectConfigCountries} from '../HConnect/hooks/useAllowedHConnectConfigCountries'
import {useCanEditGlobalCatalog} from './hooks/useCanEditGlobalCatalog'
import {useCountryConfigList, useDeleteCountryConfig} from './hooks/useCatalogConfig'
import {
  useExportCatalogConfigs,
  useImportCatalogConfigs,
  useValidateCatalogImport
} from './hooks/useCatalogImportExport'
import {CatalogBundleImportDialog} from './components/CatalogBundleImportDialog'
import {UserNameCell} from './components/UserNameCell'
import type {CountryConfigSummary} from './types'

type Row = CountryConfigSummary & {id: string}

export const CatalogConfigurationsList = () => {
  const history = useHistory()
  const allowedCountries = useAllowedHConnectConfigCountries()
  const canEditGlobal = useCanEditGlobalCatalog()
  const {data, isLoading, isError} = useCountryConfigList()
  const del = useDeleteCountryConfig()
  const exportCatalog = useExportCatalogConfigs()
  const validateCatalog = useValidateCatalogImport()
  const importCatalog = useImportCatalogConfigs()
  const [toDelete, setToDelete] = useState<Row | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  // Only surface configs for countries the user is scoped to manage.
  const allowedSet = new Set(allowedCountries.map((c) => c.toUpperCase()))
  const rows: Row[] = (data ?? [])
    .filter((r) => allowedSet.has(r.countryCode.toUpperCase()))
    .map((r) => ({...r, id: `${r.countryCode}-${r.businessLine}`}))

  const closeDeleteDialog = () => {
    del.reset()
    setToDelete(null)
  }

  const openEditor = (row: Row) =>
    history.push(`/catalog-configurations/${row.countryCode}/${row.businessLine}`)

  const columns = [
    {
      field: 'action',
      headerName: 'Action',
      renderCell: (row: Row) => (
        <>
          <IconButton
            aria-label="edit"
            style={{boxShadow: 'none'}}
            data-test-id={`edit-${row.id}`}
            onClick={() => openEditor(row)}
          >
            <ModeEditOutlineIcon style={{color: '#016AD4'}} />
          </IconButton>
          <IconButton
            aria-label="delete"
            style={{boxShadow: 'none'}}
            data-test-id={`delete-${row.id}`}
            onClick={() => setToDelete(row)}
          >
            <DeleteIcon style={{color: '#016AD4'}} />
          </IconButton>
        </>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      renderCell: (row: Row) => <Typography>{row.countryCode}</Typography>,
    },
    {
      field: 'businessLine',
      headerName: 'Business line',
      renderCell: (row: Row) => <Typography>{row.businessLine}</Typography>,
    },
    {
      field: 'updatedBy',
      headerName: 'Updated by',
      renderCell: (row: Row) => <UserNameCell userId={row.updatedBy} />,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated at',
      renderCell: (row: Row) => (
        <Typography>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</Typography>
      ),
    },
  ]

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy="page-catalog-configurations"
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          aside={
            <div style={{display: 'flex', gap: 8}}>
              <Button
                data-test-id="export-catalog-configurations-button"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={exportCatalog.isPending}
                onClick={() => exportCatalog.mutate()}
              >
                Export
              </Button>
              <Button
                data-test-id="import-catalog-configurations-button"
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setImportOpen(true)}
              >
                Import
              </Button>
              {canEditGlobal && (
                <Button
                  data-test-id="global-configuration-button"
                  variant="outlined"
                  onClick={() => history.push('/catalog-configurations/global')}
                >
                  Global metadata
                </Button>
              )}
              <Button
                data-test-id="create-catalog-configuration-button"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => history.push('/catalog-configurations/new')}
              >
                Add configuration
              </Button>
            </div>
          }
        >
          Catalog configurations
        </PageHeader>
      </div>
      <Grid item md={4} sm={0} xs={0}>
        <Paper elevation={4} style={{padding: '26px 36px'}}>
          <ResponsiveTable
            keyField="id"
            columns={columns}
            rows={isError ? [] : rows}
            emptyMessage={
              isError ? (
                <Alert severity="error">Failed to load catalog configurations.</Alert>
              ) : (
                ''
              )
            }
            tableHeight="70vh"
            onSort={() => ({})}
            loading={isLoading}
            enableHorizontalScroll={true}
            stickyHeader={true}
          />
        </Paper>
      </Grid>
      <Dialog open={Boolean(toDelete)} onClose={closeDeleteDialog}>
        <DialogTitle>Delete configuration?</DialogTitle>
        <DialogContent>
          You are about to permanently delete the configuration for{' '}
          <strong>
            {toDelete?.countryCode} · {toDelete?.businessLine}
          </strong>
          .
          <Alert severity="warning" style={{marginTop: 12}}>
            This will also delete <strong>all scopes and all their configured values</strong> for this
            country / business line, including:
            <ul style={{margin: '8px 0 0', paddingLeft: 20}}>
              <li>the catalog configuration (enabled entities, available units, fields and scope levels);</li>
              <li>every scope (Country default and any Org unit, Dispatch, Plant or Customer scopes);</li>
              <li>
                all values in those scopes — Delivery Rate, Quantity, Operational Hours, Pumps, Overbooking,
                lead times, delivery windows, etc.
              </li>
            </ul>
            <div style={{marginTop: 8}}>This cannot be undone.</div>
          </Alert>
          {del.isError && (
            <Alert severity="error" style={{marginTop: 12}}>
              {String((del.error as Error)?.message ?? 'Failed to delete configuration.')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={del.isLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={del.isLoading}
            data-test-id="confirm-delete"
            onClick={() =>
              toDelete &&
              del.mutate(
                {countryCode: toDelete.countryCode, businessLine: toDelete.businessLine},
                {onSuccess: () => setToDelete(null)}
              )
            }
          >
            {del.isLoading ? 'Deleting…' : 'Delete everything'}
          </Button>
        </DialogActions>
      </Dialog>
      <CatalogBundleImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import catalog configurations"
        busy={validateCatalog.isPending || importCatalog.isPending}
        onValidate={(bundle) => validateCatalog.mutateAsync(bundle)}
        onImport={(args) => importCatalog.mutateAsync(args)}
      />
    </Content>
  )
}
